// import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
// import { AuthUser, getCurrentUser, signIn, signOut, fetchAuthSession } from 'aws-amplify/auth';

// // Define the AuthContext type
// interface AuthContextType {
//   user: AuthUser | null;
//   isAuthenticated: boolean;
//   signIn: (username: string, password: string) => Promise<AuthUser | null>;
//   signOut: () => Promise<void>;
//   loading: boolean;
//   error: string | null; // Optional: To handle errors
//   getAuthToken: () => Promise<string | undefined>;
// }

// // Create the AuthContext
// const AuthContext = createContext<AuthContextType | undefined>(undefined);

// // AuthProvider component
// export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
//   const [user, setUser] = useState<AuthUser | null>(null);
//   const [loading, setLoading] = useState<boolean>(true);
//   const [error, setError] = useState<string | null>(null);

//   useEffect(() => {
//     // Check if there's an authenticated user when the app loads
//     const checkUser = async () => {
//       try {
//         const currentUser = await getCurrentUser();
//         setUser(currentUser as AuthUser);
//       } catch (err) {
//         setUser(null);
//       } finally {
//         setLoading(false);
//       }
//     };

//     checkUser();
//   }, []);

//   // Sign in handler
//   const handleSignIn = async (username: string, password: string): Promise<AuthUser | null> => {
//     try {
//       const result = await signIn({ username: username, password: password });
//       setError(null); // Reset error on success
//       return getCurrentUser();
//     } catch (err: any) {
//       setError(err.message || 'Error signing in');
//       return null;
//     }
//   };

//   // Sign out handler
//   const handleSignOut = async () => {
//     try {
//       await signOut();
//       setUser(null);
//     } catch (err: any) {
//       setError(err.message || 'Error signing out');
//     }
//   };

//   const handleGetAuthToken = async (): Promise<string | undefined> => {
//     try {
//       const tokens = (await fetchAuthSession()).tokens;
//       return tokens?.idToken?.toString();
//     } catch (err) {
//       return undefined;
//     }
//   };

//   return (
//     <AuthContext.Provider
//       value={{
//         user,
//         isAuthenticated: !!user,
//         signIn: handleSignIn,
//         signOut: handleSignOut,
//         loading,
//         error,
//         getAuthToken: handleGetAuthToken,
//       }}
//     >
//       {children}
//     </AuthContext.Provider>
//   );
// };

// // Custom hook to access AuthContext
// export const useAuth = () => {
//   const context = useContext(AuthContext);
//   if (!context) {
//     throw new Error('useAuth must be used within an AuthProvider');
//   }
//   return context;
// };

// AuthContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthUser, getCurrentUser, signIn, signOut, fetchAuthSession } from 'aws-amplify/auth';
import { setAxiosAuthInterceptor } from '../services/apiService';

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  signIn: (username: string, password: string) => Promise<AuthUser | null>;
  signOut: () => Promise<void>;
  loading: boolean;
  gotToVerify: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [goToVerify, setGoToVerify] = useState<boolean>(false);


  const getAuthToken = async (): Promise<string | undefined> => {
    try {
      const tokens = (await fetchAuthSession()).tokens;
      return tokens?.idToken?.toString();
    } catch (err) {
      return undefined;
    }
  };

  useEffect(() => {
    // Set Axios interceptor with the current auth token
    setAxiosAuthInterceptor(getAuthToken);

    const checkUser = async () => {
      try {
        const currentUser = await getCurrentUser();
        setUser(currentUser as AuthUser);
      } catch (err) {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    checkUser();
  }, []);

  const handleSignIn = async (username: string, password: string): Promise<AuthUser | null> => {
    try {
      const result = await signIn({ username, password });
      setError(null);
      if (result.nextStep.signInStep == 'CONFIRM_SIGN_UP') {
        setGoToVerify(true);
        return null;
      } else {
        setGoToVerify(false);
        setUser(await getCurrentUser());
        return user;
      }
    } catch (err: any) {
      setError(err.message || 'Error signing in');
      throw new Error(err);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      setUser(null);
      localStorage.setItem('logout', Date.now().toString());
    } catch (err: any) {
      setError(err.message || 'Error signing out');
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        signIn: handleSignIn,
        signOut: handleSignOut,
        gotToVerify: goToVerify,
        loading,
        error,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
